import React from 'react';
import { format } from 'date-fns';
import UserRow from './UserRow';
import LiquidFillLoader from '../../../shared/components/ui/LiquidFillLoader';
import { DEFAULT_COLUMNS, AVAILABLE_COLUMNS } from '../config/userColumns';
import { TABLE_STYLES } from '../styles/dataTableStyles';

const UsersTable = ({
  users,
  loading,
  loadingMore = false,
  error,
  totalUsers,
  hasMore = false,
  onLoadMore,
  hasSearchFilter,
  searchTerm,
  columnConfig,
  onRowClick
}) => {
  const config = columnConfig || DEFAULT_COLUMNS;
  const visibleOrder = (config.order || []).filter(key =>
    (config.enabled || []).includes(key) && AVAILABLE_COLUMNS[key]
  );
  const styles = TABLE_STYLES;

  if (loading) {
    return (
      <div className="overflow-hidden rounded-lg" style={{ backgroundColor: styles.primaryBg, border: `1px solid ${styles.borderColor}` }}>
        <div className="flex flex-col items-center justify-center px-6 py-12">
          <LiquidFillLoader size={100} />
          <span className="mt-4 text-sm" style={{ color: styles.textSecondary, fontFamily: 'Raleway, sans-serif' }}>
            Loading users...
          </span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="overflow-hidden rounded-lg" style={{ backgroundColor: styles.primaryBg, border: `1px solid ${styles.borderColor}` }}>
        <div className="px-6 py-8 text-center" style={{ color: '#ef4444', fontFamily: 'Raleway, sans-serif' }}>
          Error: {error}
        </div>
      </div>
    );
  }

  if (!users || users.length === 0) {
    return (
      <div className="overflow-hidden rounded-lg" style={{ backgroundColor: styles.primaryBg, border: `1px solid ${styles.borderColor}` }}>
        <div className="px-6 py-8 text-center" style={{ color: styles.textSecondary, fontFamily: 'Raleway, sans-serif' }}>
          {hasSearchFilter && searchTerm ? (
            <>No users found matching your search.</>
          ) : (
            <>No users found.</>
          )}
        </div>
      </div>
    );
  }

  const lastResultDate = users.length > 0 && (users[0].lastVisitTime || users[0].lastSession)
    ? format(new Date(users[0].lastVisitTime || users[0].lastSession), 'MMM d, yyyy, h:mm:ss a')
    : null;

  return (
    <>
      <div className="mb-2 px-1 text-left">
        <div className="text-xs text-[#475569]" style={{ fontFamily: 'Raleway, sans-serif' }}>
          Showing <span className="font-semibold">{users.length}</span> most recent results of{' '}
          {Number(totalUsers || 0).toLocaleString()} matches
        </div>
      </div>

      <div className="overflow-hidden rounded-lg" style={{ backgroundColor: styles.primaryBg, border: `1px solid ${styles.borderColor}` }}>
        <div className="overflow-x-auto">
          <table className="w-full" style={{ fontFamily: 'Raleway, sans-serif' }}>
            <thead className="sticky top-0 z-10 bg-gray-100">
              <tr style={{ borderBottom: `2px solid ${styles.borderColor}` }}>
                {visibleOrder.map((key) => {
                  const col = AVAILABLE_COLUMNS[key];
                  const label = col ? col.label : key;
                  const isFirst = visibleOrder[0] === key;
                  return (
                    <th
                      key={key}
                      className={`px-6 py-3 text-xs font-semibold uppercase tracking-wider bg-gray-100 ${
                        isFirst ? 'text-left' : 'text-center'
                      }`}
                      style={{ color: styles.headerText }}
                    >
                      {label}
                    </th>
                  );
                })}
                <th className="px-6 py-3 text-right text-xs font-semibold uppercase tracking-wider bg-gray-100" style={{ color: styles.headerText }} />
              </tr>
            </thead>
            <tbody>
              {users.map((user, index) => {
                const userId = user.distinctId || user.userId;
                return (
                  <UserRow
                    key={`${userId}-${index}`}
                    user={user}
                    onRowClick={onRowClick}
                    columns={visibleOrder}
                    styles={styles}
                  />
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {hasMore && !loading && !error && users.length > 0 && (
        <div className="mt-3 flex flex-col items-center gap-2">
          {lastResultDate && (
            <div className="text-xs text-[#475569]" style={{ fontFamily: 'Raleway, sans-serif' }}>
              Showing {users.length} results through {lastResultDate}
            </div>
          )}
          <button
              onClick={onLoadMore}
              disabled={loadingMore}
              className="px-4 py-2 text-sm font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              style={{
                fontFamily: 'Raleway, sans-serif',
                backgroundColor: loadingMore ? styles.secondaryBg : styles.primaryBg,
                color: loadingMore ? styles.textSecondary : styles.textPrimary,
                border: `1px solid ${styles.borderColor}`
              }}
            >
              {loadingMore ? 'Loading...' : 'Load More'}
            </button>
        </div>
      )}
    </>
  );
};

export default UsersTable;
